import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box } from '@mui/material';
import useProductFilterSWR from '../../../../hooks/products/useProductFilterSWR';
import { CollectionLoadingLayout } from '../../../layouts/LoadingLayouts/CollectionLoadingLayout';
import SearchHintItem from './SearchHintItem';
import useQuotation from '../../../../hooks/quotations/useQuotation';
import { ProductSearch } from '../../../../models/ProductSearch';
import ButtonOutlined from '../../../shared/Buttons/ButtonOutlined';
import useFullScreenLoading from '../../../../hooks/useFullScreenLoading';
import SnackbarUtils from '../../../../utils/SnackbarUtils';
import { mutate } from 'swr';
import { QUOTES_URL } from 'hooks/quotations/useQuotationSWR';

export const SearchHints: FC<{
    productsList: ProductSearch[];
    searchItem: string;
    quotationId: number;
    closeDialog: () => void;
    productListChanged: (products: ProductSearch[]) => void;
}> = ({
    productsList,
    searchItem,
    quotationId,
    closeDialog,
    productListChanged,
}) => {
    const { t } = useTranslation('quotations', {
        keyPrefix: 'QUOTATIONS.QUOTATION_EDITOR.ADD_PRODUCT_DIALOG',
    });
    const [pagination, setPagination] = useState<number>(1);
    const fullScreenLoading = useFullScreenLoading();

    const { products, count, isLoading, isError } = useProductFilterSWR({
        collectionIds: [],
        search: searchItem,
        perPage: 10,
        page: pagination,
        sortKey: 'ALPHABETICAL_ASC',
        tags: [],
    });

    const handlePagination = () => {
        setPagination((current) => current + 1);
    };

    const quotation = useQuotation();

    useEffect((): void => {
        if (products) productListChanged([...productsList, ...products]);
    }, [products]);

    const addProduct = (product) => {
        fullScreenLoading.setFullScreenLoading(true);
        quotation
            .addProductsToQuotation({
                products: {
                    items: [
                        {
                            sku: product.sku,
                            quantity: 1,
                        },
                    ],
                },
                quotationId: quotationId,
            })
            .then(() => {
                SnackbarUtils.success(t('ADDED_SUCCESSFUL').toString());
                mutate(QUOTES_URL + `/${quotationId}`);
                closeDialog();
            })
            .catch(() => {
                SnackbarUtils.error(t('ERROR').toString());
            })
            .finally(() => {
                fullScreenLoading.setFullScreenLoading(false);
            });
    };

    return (
        <>
            {productsList && productsList.length > 0 && (
                <Box
                    sx={{
                        width: '100%',
                        paddingTop: '10px',
                        border: '1px solid #f5f5f5',
                        borderRadius: '5px',
                    }}
                >
                    {productsList.map((product) => {
                        return (
                            <SearchHintItem
                                product={product}
                                key={product.id}
                                handleAddProduct={addProduct}
                            />
                        );
                    })}
                </Box>
            )}

            <Box
                sx={{
                    width: '100%',
                    paddingTop: '10px',
                }}
            >
                {isLoading && <CollectionLoadingLayout />}
                {isError && <Box>{t('REQUEST_ERROR').toUpperCase()}</Box>}
                {productsList && productsList.length == 0 && !isLoading && (
                    <Box
                        sx={{
                            display: 'flex',
                            justifyContent: 'center',
                            padding: '10px 0',
                            fontSize: '16px',
                        }}
                    >
                        {t('NO_PRODUCTS').toUpperCase()}
                    </Box>
                )}
            </Box>
            {count != productsList.length && !isLoading && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        padding: '10px 0',
                    }}
                >
                    <ButtonOutlined onClick={handlePagination}>
                        {t('LOAD_MORE')}
                    </ButtonOutlined>
                </Box>
            )}
        </>
    );
};
